/**
 * CareerOrientationTeaserPage.tsx
 *
 * Marketing page for guest users who haven't logged in.
 * Introduces the Career Orientation (CAT) tool  -  khám phá bản thân, tiềm năng nghề nghiệp.
 * Design adapted from CATProfileTeaserPage.tsx.
 */

import { useState, useEffect } from 'react';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { SEOHead } from '../components/SEOHead';
import { getCanonicalUrl } from '../utils/siteConfig';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@vsat/shared/lib/api';

export default function CareerOrientationTeaserPage() {
  const navigate = useNavigate();
  const [completedCount, setCompletedCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await apiService.getCareerOrientationPublicStats();
        setCompletedCount(data.totalAssessments);
      } catch {
        // Silently fail  -  stats are not critical
      }
    };
    fetchStats();
  }, []);

  const handleLoginClick = () => {
    localStorage.setItem('redirectAfterLogin', '/huong-nghiep-cat?screen=instruction');
    navigate('/login');
  };

  return (
    <div className='min-h-screen bg-background-light dark:bg-background-dark flex flex-col'>
      <SEOHead
        title='Khám phá bản thân | Hướng nghiệp CAT'
        description='Khám phá tiềm năng nghề nghiệp của bạn với bài đánh giá CAT miễn phí. Tìm hiểu sở thích, năng lực và định hướng ngành học phù hợp.'
        canonicalUrl={getCanonicalUrl('/kham-pha-ban-than')}
        keywords='trắc nghiệm hướng nghiệp, khám phá bản thân, định hướng nghề nghiệp, CAT, hướng nghiệp miễn phí'
      />
      <UnifiedHeader />

      <main className='flex-1 pt-20 pb-12 lg:pt-24'>
        {/* ===== HERO SECTION ===== */}
        <section className='relative overflow-hidden px-4 sm:px-6 lg:px-8 py-20 lg:py-32'>
          <div className='absolute inset-0 -z-10 bg-gradient-to-br from-[#D4841C] via-[#E8A84C] to-[#F5C97A] opacity-10' />
          <div className='absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#D4841C]/20 via-transparent to-transparent' />
          {/* Floating geometric shapes */}
          <div
            className='absolute top-20 right-[10%] h-20 w-20 rounded-full border-4 border-[#D4841C]/10 animate-bounce'
            style={{ animationDuration: '6s' }}
          />
          <div className='absolute bottom-40 left-[5%] h-12 w-12 rotate-45 border border-[#D4841C]/20 bg-[#D4841C]/5 animate-pulse' />
          <div className='absolute top-[40%] right-[25%] h-4 w-4 rounded-full bg-[#E8A84C]/30 blur-sm animate-pulse' />
          <div
            className='absolute bottom-[20%] right-[10%] h-32 w-32 rounded-full border border-dashed border-[#D4841C]/20 opacity-30'
            style={{ animation: 'spin 12s linear infinite' }}
          />

          <div className='mx-auto max-w-7xl text-center relative z-10'>
            {/* Badge */}
            <div className='inline-flex items-center gap-2 rounded-full border border-yellow-400/30 bg-yellow-500/10 px-4 py-1.5 mb-8 shadow-md shadow-yellow-500/10 backdrop-blur-sm'>
              <span className='material-symbols-outlined text-yellow-500 text-sm animate-pulse'>
                explore
              </span>
              <span className='text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-yellow-600 to-yellow-400 bg-clip-text text-transparent'>
                Miễn phí
              </span>
              <span className='material-symbols-outlined text-yellow-500 text-sm animate-pulse'>
                explore
              </span>
            </div>

            <h1 className='mx-auto max-w-5xl text-5xl font-black leading-tight tracking-tight text-slate-900 dark:text-white lg:text-7xl'>
              Khám Phá{' '}
              <span className='text-transparent bg-clip-text bg-gradient-to-r from-[#D4841C] via-[#E8A84C] to-[#F5C97A]'>
                Bản Thân
              </span>
            </h1>
            <p className='mx-auto mt-6 max-w-2xl text-lg font-medium text-slate-600 dark:text-slate-300 lg:text-xl'>
              Khám phá tiềm năng nghề nghiệp của bạn. Bài đánh giá CAT giúp bạn hiểu rõ sở thích,
              năng lực và tìm ra hướng đi phù hợp nhất.
            </p>

            <div className='mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row'>
              <button
                onClick={handleLoginClick}
                className='relative overflow-hidden group flex h-14 min-w-[220px] items-center justify-center rounded-2xl bg-gradient-to-r from-[#D4841C] to-[#E8A84C] px-8 text-lg font-bold text-white shadow-xl shadow-[#D4841C]/30 transition-all hover:translate-y-[-2px] hover:shadow-2xl'
              >
                <span className='relative z-10 flex items-center gap-2'>
                  Đăng nhập để bắt đầu{' '}
                  <span className='material-symbols-outlined group-hover:translate-x-1 transition-transform'>
                    arrow_forward
                  </span>
                </span>
                <div className='absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300' />
              </button>
            </div>
            <p className='mt-4 text-xs font-medium text-slate-500'>
              Đăng nhập và bắt đầu hành trình khám phá bản thân. Hoàn toàn miễn phí!
            </p>
          </div>
        </section>

        {/* ===== FEATURE CARDS ===== */}
        <section className='px-4 sm:px-6 lg:px-8 pb-24 pt-10 -mt-10'>
          <div className='mx-auto max-w-7xl'>
            <div className='grid gap-8 md:grid-cols-3'>
              {/* Card 1: Personality Assessment */}
              <div className='group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-200/50 transition-all hover:shadow-xl hover:translate-y-[-4px] dark:border-slate-700 dark:bg-slate-800 dark:shadow-none'>
                <div className='rounded-2xl bg-slate-50 p-6 h-full flex flex-col dark:bg-slate-900/50'>
                  <div className='mb-4 flex items-center justify-between'>
                    <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#D4841C] to-[#E8A84C] shadow-lg shadow-[#D4841C]/30'>
                      <span className='material-symbols-outlined text-white text-xl'>
                        psychology
                      </span>
                    </div>
                    <span className='material-symbols-outlined text-[#E8A84C]/40 text-3xl group-hover:text-[#D4841C]/60 transition-colors'>
                      arrow_outward
                    </span>
                  </div>
                  <h3 className='text-xl font-bold text-slate-900 dark:text-white mb-3'>
                    Đánh giá sở thích nghề nghiệp
                  </h3>
                  <p className='text-sm leading-relaxed text-slate-500 dark:text-slate-400 flex-1'>
                    Bài kiểm tra dựa trên mô hình Holland (RIASEC) giúp bạn xác định nhóm sở thích
                    nghề nghiệp phù hợp nhất.
                  </p>
                  <div className='mt-4 pt-4 border-t border-slate-200/50 dark:border-slate-700/50'>
                    <span className='text-xs font-semibold text-[#D4841C] dark:text-[#E8A84C]'>
                      6 nhóm nghề nghiệp • 48 câu hỏi
                    </span>
                  </div>
                </div>
              </div>

              {/* Card 2: Free Quick Result */}
              <div className='group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-200/50 transition-all hover:shadow-xl hover:translate-y-[-4px] dark:border-slate-700 dark:bg-slate-800 dark:shadow-none'>
                <div className='rounded-2xl bg-slate-50 p-6 h-full flex flex-col dark:bg-slate-900/50'>
                  <div className='mb-4 flex items-center justify-between'>
                    <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#E8A84C] to-[#F5C97A] shadow-lg shadow-[#E8A84C]/30'>
                      <span className='material-symbols-outlined text-white text-xl'>
                        bar_chart
                      </span>
                    </div>
                    <span className='material-symbols-outlined text-[#E8A84C]/40 text-3xl group-hover:text-[#D4841C]/60 transition-colors'>
                      arrow_outward
                    </span>
                  </div>
                  <h3 className='text-xl font-bold text-slate-900 dark:text-white mb-3'>
                    Kết quả nhanh miễn phí
                  </h3>
                  <p className='text-sm leading-relaxed text-slate-500 dark:text-slate-400 flex-1'>
                    Nhận ngay kết quả sơ bộ miễn phí với biểu đồ radar về 6 nhóm năng lực và top 3
                    nhóm nghề nghiệp phù hợp nhất.
                  </p>
                  <div className='mt-4 pt-4 border-t border-slate-200/50 dark:border-slate-700/50'>
                    <span className='text-xs font-semibold text-[#D4841C] dark:text-[#E8A84C]'>
                      Biểu đồ trực quan • Nhận kết quả ngay
                    </span>
                  </div>
                </div>
              </div>

              {/* Card 3: Full Report */}
              <div className='group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-200/50 transition-all hover:shadow-xl hover:translate-y-[-4px] dark:border-slate-700 dark:bg-slate-800 dark:shadow-none'>
                <div className='rounded-2xl bg-slate-50 p-6 h-full flex flex-col dark:bg-slate-900/50'>
                  <div className='mb-4 flex items-center justify-between'>
                    <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#C07018] to-[#D4841C] shadow-lg shadow-[#C07018]/30'>
                      <span className='material-symbols-outlined text-white text-xl'>
                        description
                      </span>
                    </div>
                    <span className='material-symbols-outlined text-[#E8A84C]/40 text-3xl group-hover:text-[#D4841C]/60 transition-colors'>
                      arrow_outward
                    </span>
                  </div>
                  <h3 className='text-xl font-bold text-slate-900 dark:text-white mb-3'>
                    Báo cáo chi tiết
                  </h3>
                  <p className='text-sm leading-relaxed text-slate-500 dark:text-slate-400 flex-1'>
                    Báo cáo phân tích chuyên sâu với danh sách ngành học, trường đại học phù hợp, lộ
                    trình nghề nghiệp chi tiết theo từng cấp bậc.
                  </p>
                  <div className='mt-4 pt-4 border-t border-slate-200/50 dark:border-slate-700/50'>
                    <span className='text-xs font-semibold text-[#D4841C] dark:text-[#E8A84C]'>
                      Phân tích chuyên sâu • Xuất PDF
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className='px-4 sm:px-6 lg:px-8 pb-24'>
          <div className='mx-auto max-w-5xl'>
            <h2 className='text-center text-3xl font-black text-slate-900 dark:text-white lg:text-4xl mb-16'>
              Quy trình đánh giá
            </h2>
            <div className='grid gap-8 md:grid-cols-4'>
              {[
                {
                  step: '01',
                  icon: 'login',
                  title: 'Đăng nhập',
                  desc: 'Tạo tài khoản hoặc đăng nhập để bắt đầu bài đánh giá',
                  color: 'from-[#C07018] to-[#D4841C]',
                },
                {
                  step: '02',
                  icon: 'quiz',
                  title: 'Trả lời câu hỏi',
                  desc: 'Hoàn thành bài kiểm tra với 48 câu hỏi về sở thích nghề nghiệp',
                  color: 'from-[#D4841C] to-[#E8A84C]',
                },
                {
                  step: '03',
                  icon: 'analytics',
                  title: 'Nhận kết quả',
                  desc: 'Xem kết quả sơ bộ miễn phí với biểu đồ radar các nhóm năng lực',
                  color: 'from-[#E8A84C] to-[#F5C97A]',
                },
                {
                  step: '04',
                  icon: 'school',
                  title: 'Khám phá ngành',
                  desc: 'Tìm hiểu ngành học, trường đại học và lộ trình nghề nghiệp phù hợp',
                  color: 'from-[#D4841C] to-[#C07018]',
                },
              ].map(item => (
                <div key={item.step} className='text-center'>
                  <div
                    className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${item.color} shadow-lg`}
                  >
                    <span className='material-symbols-outlined text-white text-xl'>
                      {item.icon}
                    </span>
                  </div>
                  <div className='text-xs font-bold text-slate-400 mb-2'>BƯỚC {item.step}</div>
                  <h3 className='text-lg font-bold text-slate-900 dark:text-white mb-2'>
                    {item.title}
                  </h3>
                  <p className='text-sm text-slate-500 dark:text-slate-400'>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== STATS ===== */}
        <section className='px-4 sm:px-6 lg:px-8 pb-24'>
          <div className='mx-auto max-w-5xl'>
            <div className='rounded-3xl bg-gradient-to-r from-[#C07018] via-[#D4841C] to-[#E8A84C] p-[1px]'>
              <div className='rounded-3xl bg-white dark:bg-slate-900 p-8 lg:p-12'>
                <div className='grid grid-cols-2 lg:grid-cols-5 gap-8'>
                  {[
                    {
                      value:
                        completedCount !== null
                          ? completedCount.toLocaleString('vi-VN') + '+'
                          : '...',
                      label: 'Học sinh đã tìm ra hướng đi',
                    },
                    { value: '48', label: 'Câu hỏi đánh giá' },
                    { value: '6', label: 'Nhóm nghề nghiệp' },
                    { value: '100+', label: 'Ngành học phù hợp' },
                    { value: 'Miễn phí', label: 'Bài đánh giá sơ bộ' },
                  ].map(stat => (
                    <div key={stat.label} className='text-center'>
                      <div className='text-3xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#D4841C] to-[#E8A84C]'>
                        {stat.value}
                      </div>
                      <div className='mt-1 text-xs font-semibold text-slate-500 uppercase tracking-wider'>
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== BOTTOM CTA ===== */}
        <section className='px-4 sm:px-6 lg:px-8 pb-24'>
          <div className='mx-auto max-w-5xl'>
            <div className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-10 lg:p-16 text-center text-white'>
              {/* Decorative elements */}
              <div className='absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#E8A84C]/50 to-transparent' />
              <div className='absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#D4841C]/10 via-transparent to-transparent' />

              <div className='relative z-10 flex flex-col items-center'>
                <h2 className='text-3xl font-black lg:text-5xl mb-6'>
                  Sẵn sàng khám phá bản thân?
                </h2>
                <p className='mb-10 text-lg text-slate-300 max-w-lg mx-auto'>
                  Đăng nhập và bắt đầu hành trình khám phá năng lực, định hướng nghề nghiệp của bạn.
                </p>
                <button
                  onClick={handleLoginClick}
                  className='group relative flex h-16 items-center justify-center rounded-2xl bg-white px-10 text-xl font-bold text-slate-900 transition-all hover:scale-105 hover:bg-slate-50 hover:shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)]'
                >
                  <span className='mr-2'>Đăng nhập ngay</span>
                  <span className='material-symbols-outlined transition-transform group-hover:translate-x-1'>
                    arrow_forward
                  </span>
                </button>
                <p className='mt-6 text-sm text-slate-400 font-medium'>
                  <span className='inline-block h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse' />
                  Hoàn toàn miễn phí
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <UnifiedFooter />
    </div>
  );
}
